import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { CA_COUNTIES as COUNTIES_REF } from '@lds-efsp/shared-contracts';
import { useOrderForm } from '../contexts/OrderFormContext';
import DocumentUploader from '../components/DocumentUploader';
import OrderSummaryPanel from '../components/OrderSummaryPanel';
import PricingEstimate from '../components/PricingEstimate';
import FormInput, { FormSelect, FormCheckbox } from '../../setup/components/FormInput';

type Step = 'retrieval-info' | 'documents' | 'case-details' | 'authorization' | 'review';

const STEPS: { id: Step; label: string }[] = [
  { id: 'retrieval-info', label: 'Retrieval Info' },
  { id: 'documents', label: 'Documents' },
  { id: 'case-details', label: 'Case Details' },
  { id: 'authorization', label: 'Authorization' },
  { id: 'review', label: 'Review & Submit' },
];

const RETRIEVAL_TYPES = [
  { value: '', label: 'Select retrieval type...' },
  { value: 'same-case', label: 'Same Case' },
  { value: 'different-case', label: 'Different Case' },
];

const LOCATION_TYPES = [
  { value: '', label: 'Select location type...' },
  { value: 'state-court', label: 'State Court' },
  { value: 'county-recorders', label: 'County Recorders' },
  { value: 'secretary-of-state', label: 'Secretary of State' },
  { value: 'federal-court', label: 'Federal Court' },
  { value: 'archives', label: 'Archives' },
];

const COPY_TYPES = [
  { value: 'plain', label: 'Plain Copy' },
  { value: 'certified', label: 'Certified Copy' },
];

const countyOptions = [
  { value: '', label: 'Select county...' },
  ...COUNTIES_REF.map(c => ({ value: c.name, label: c.name })),
];

interface DocumentRetrievalFormProps {
  brand: BrandConfig;
}

const DocumentRetrievalForm: React.FC<DocumentRetrievalFormProps> = ({ brand }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('retrieval-info');
  const {
    caseNumber,
    caseTitle,
    priority,
    typeData,
    documents,
    updateCommonFields,
    updateTypeData,
    uploadDocument,
    removeDocument,
    submitOrder,
  } = useOrderForm();

  const data = typeData as {
    retrievalType?: string;
    locationType?: string;
    locationState?: string;
    locationCounty?: string;
    caseNumberToCopy?: string;
    isEntireFile?: boolean;
    isCertainDocuments?: boolean;
    certainDocumentNames?: string;
    isRegisterOfAction?: boolean;
    copyType?: string;
    pageLimit?: number;
    authorizeCourtFees?: boolean;
    searchTarget?: string;
    searchLocation?: string;
  };

  const stepIndex = STEPS.findIndex(s => s.id === currentStep);

  return (
    <div className="flex gap-6">
      <div className="flex-1">
        {/* Step navigation */}
        <div className="flex border-b mb-6" style={{ borderColor: brand.colors.border }}>
          {STEPS.map((step, i) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(step.id)}
              className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: currentStep === step.id ? brand.colors.primary : 'transparent',
                color: currentStep === step.id ? brand.colors.link : brand.colors.textLight,
              }}
            >
              {i + 1}. {step.label}
            </button>
          ))}
        </div>

        {currentStep === 'retrieval-info' && (
          <div className="space-y-6">
            <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>Retrieval Information</h3>
              <FormSelect brand={brand} label="Retrieval Type" name="retrievalType" value={data.retrievalType ?? ''} onChange={(e) => updateTypeData({ retrievalType: e.target.value } as Partial<typeof typeData>)} options={RETRIEVAL_TYPES} required />
              {data.retrievalType === 'different-case' && (
                <>
                  <FormSelect brand={brand} label="Location Type" name="locationType" value={data.locationType ?? ''} onChange={(e) => updateTypeData({ locationType: e.target.value } as Partial<typeof typeData>)} options={LOCATION_TYPES} />
                  <FormInput brand={brand} label="State" name="locationState" value={data.locationState ?? 'CA'} onChange={(e) => updateTypeData({ locationState: e.target.value } as Partial<typeof typeData>)} />
                  <FormSelect brand={brand} label="County" name="locationCounty" value={data.locationCounty ?? ''} onChange={(e) => updateTypeData({ locationCounty: e.target.value } as Partial<typeof typeData>)} options={countyOptions} />
                </>
              )}
            </div>
            <PricingEstimate brand={brand} county={data.locationCounty ?? ''} priority={priority} />
          </div>
        )}

        {currentStep === 'case-details' && (
          <div className="space-y-6">
            <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>Case Details</h3>
              <FormInput brand={brand} label="Case Number" name="caseNumber" value={caseNumber} onChange={(e) => updateCommonFields({ caseNumber: e.target.value })} required />
              <FormInput brand={brand} label="Case Title" name="caseTitle" value={caseTitle} onChange={(e) => updateCommonFields({ caseTitle: e.target.value })} />
              <FormInput brand={brand} label="Case Number to Copy" name="caseNumberToCopy" value={data.caseNumberToCopy ?? ''} onChange={(e) => updateTypeData({ caseNumberToCopy: e.target.value } as Partial<typeof typeData>)} />
              <FormCheckbox brand={brand} label="Entire file" name="isEntireFile" checked={data.isEntireFile ?? false} onChange={(e) => updateTypeData({ isEntireFile: e.target.checked } as Partial<typeof typeData>)} />
              <FormCheckbox brand={brand} label="Certain documents" name="isCertainDocuments" checked={data.isCertainDocuments ?? false} onChange={(e) => updateTypeData({ isCertainDocuments: e.target.checked } as Partial<typeof typeData>)} />
              {data.isCertainDocuments && (
                <FormInput brand={brand} label="Document Names" name="certainDocumentNames" value={data.certainDocumentNames ?? ''} onChange={(e) => updateTypeData({ certainDocumentNames: e.target.value } as Partial<typeof typeData>)} helpText="List the specific document names" />
              )}
              <FormCheckbox brand={brand} label="Register of Action" name="isRegisterOfAction" checked={data.isRegisterOfAction ?? false} onChange={(e) => updateTypeData({ isRegisterOfAction: e.target.checked } as Partial<typeof typeData>)} />
              <FormSelect brand={brand} label="Copy Type" name="copyType" value={data.copyType ?? 'plain'} onChange={(e) => updateTypeData({ copyType: e.target.value } as Partial<typeof typeData>)} options={COPY_TYPES} />
              <FormInput brand={brand} label="Page Limit" name="pageLimit" type="number" value={data.pageLimit ?? 0} onChange={(e) => updateTypeData({ pageLimit: Number(e.target.value) } as Partial<typeof typeData>)} helpText="0 for no limit, max 9999" />
            </div>
          </div>
        )}

        {currentStep === 'documents' && (
          <DocumentUploader brand={brand} documents={documents} onUpload={uploadDocument} onRemove={removeDocument} />
        )}

        {currentStep === 'authorization' && (
          <div className="space-y-6">
            <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>Authorization</h3>
              <FormCheckbox brand={brand} label="Authorize court fees" name="authorizeCourtFees" checked={data.authorizeCourtFees ?? true} onChange={(e) => updateTypeData({ authorizeCourtFees: e.target.checked } as Partial<typeof typeData>)} />
              <FormInput brand={brand} label="Search Target" name="searchTarget" value={data.searchTarget ?? ''} onChange={(e) => updateTypeData({ searchTarget: e.target.value } as Partial<typeof typeData>)} />
              <FormInput brand={brand} label="Search Location" name="searchLocation" value={data.searchLocation ?? ''} onChange={(e) => updateTypeData({ searchLocation: e.target.value } as Partial<typeof typeData>)} />
            </div>
          </div>
        )}

        {currentStep === 'review' && (
          <OrderSummaryPanel brand={brand} onSubmit={async () => {
            try {
              await submitOrder();
              navigate('/orders');
            } catch { /* Error displayed in panel */ }
          }} />
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center pt-4 mt-6 border-t" style={{ borderColor: brand.colors.border }}>
          <button
            onClick={() => { if (stepIndex > 0) setCurrentStep(STEPS[stepIndex - 1].id); }}
            disabled={stepIndex === 0}
            className="px-6 py-2 rounded-lg text-sm font-medium border"
            style={{ borderColor: brand.colors.border, opacity: stepIndex === 0 ? 0.5 : 1 }}
          >
            Previous
          </button>
          {stepIndex < STEPS.length - 1 && (
            <button
              onClick={() => setCurrentStep(STEPS[stepIndex + 1].id)}
              className="px-6 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Next
            </button>
          )}
        </div>
      </div>

      <div className="w-80 flex-shrink-0 hidden lg:block">
        <OrderSummaryPanel brand={brand} onSubmit={async () => {
          try {
            await submitOrder();
            navigate('/orders');
          } catch { /* Error displayed in panel */ }
        }} />
      </div>
    </div>
  );
};

export default DocumentRetrievalForm;
